import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { useEffect } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, {
  Easing,
  ZoomIn,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { ROUND_KEYS, ROUND_MATCHES, type RoundKey } from '@/features/predictor/bracket';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

export interface BracketTeam {
  id: string;
  name: string;
  short_name: string | null;
  logo_url: string | null;
}

interface Tie {
  home?: string | null;
  away?: string | null;
}

/** One knockout match: two tappable team rows; the picked winner glows green. */
function MatchCard({
  matchId,
  tie,
  pickedId,
  teamById,
  onPick,
}: {
  matchId: number;
  tie: Tie | undefined;
  pickedId: string | null | undefined;
  teamById: Map<string, BracketTeam>;
  onPick: (matchId: number, teamId: string) => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ready = Boolean(tie?.home && tie?.away);
  const decided = Boolean(pickedId);

  return (
    <Card className="mb-2.5 gap-1.5 p-3">
      <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-muted">
        M{matchId}
      </AppText>
      {ready ? (
        ([tie?.home, tie?.away] as (string | null | undefined)[]).map((tid, i) => {
          const team = tid ? teamById.get(tid) : undefined;
          const isWinner = decided && pickedId === tid;
          return (
            <Pressable
              key={`${matchId}-${tid ?? i}`}
              accessibilityRole="button"
              accessibilityState={{ selected: isWinner }}
              onPress={() => tid && onPick(matchId, tid)}
              className={`flex-row items-center gap-3 rounded-xl border px-3 py-2.5 ${
                isWinner
                  ? 'border-win/50 bg-win/10'
                  : decided
                    ? 'border-line bg-surface opacity-45'
                    : 'border-line bg-surface active:bg-surface-2'
              }`}
            >
              <TeamCrest uri={team?.logo_url} name={localizeTeamName(team, locale)} size="sm" />
              <AppText variant="caption" weight="semibold" className="flex-1 text-ink" numberOfLines={1}>
                {localizeTeamName(team, locale, true)}
              </AppText>
              {isWinner ? (
                <Ionicons name="checkmark-circle" size={18} color={palette.win} />
              ) : null}
            </Pressable>
          );
        })
      ) : (
        <AppText variant="caption" className="py-3 text-center text-muted">
          {t('predictor.awaiting')}
        </AppText>
      )}
    </Card>
  );
}

/**
 * Celebratory champion banner: the World Cup logo, a gold crown, and the
 * champion's flag in a glowing circle (centre-cropped so it never stretches).
 * Zooms in when the final is decided, with a soft pulsing glow.
 */
function ChampionBanner({ champion, wcLogo }: { champion: BracketTeam; wcLogo: string | null }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const reduce = useReducedMotion();
  const glow = useSharedValue(0);
  useEffect(() => {
    if (reduce) return;
    glow.value = withRepeat(
      withTiming(1, { duration: 1500, easing: Easing.inOut(Easing.ease) }),
      -1,
      true,
    );
  }, [reduce, glow]);
  const glowStyle = useAnimatedStyle(() => ({
    opacity: 0.25 + glow.value * 0.45,
    transform: [{ scale: 0.9 + glow.value * 0.22 }],
  }));

  return (
    <Animated.View entering={reduce ? undefined : ZoomIn.springify().damping(13)} className="mx-4">
      <Card
        elevated
        className="items-center gap-2.5 overflow-hidden p-5"
        style={{ borderColor: palette.captain, borderWidth: 1.5 }}
      >
        <View className="flex-row items-center gap-5">
          <Ionicons name="sparkles" size={13} color={palette.captain} />
          {wcLogo ? (
            <Image source={{ uri: wcLogo }} style={{ width: 30, height: 30 }} contentFit="contain" cachePolicy="memory-disk" />
          ) : (
            <Ionicons name="trophy" size={24} color={palette.captain} />
          )}
          <Ionicons name="sparkles" size={13} color={palette.captain} />
        </View>

        <View className="items-center justify-center">
          <Animated.View
            style={[
              { position: 'absolute', width: 86, height: 86, borderRadius: 43, backgroundColor: palette.captain },
              glowStyle,
            ]}
          />
          <View
            className="items-center justify-center overflow-hidden rounded-full"
            style={{ width: 74, height: 74, borderWidth: 2.5, borderColor: palette.captain }}
          >
            {champion.logo_url ? (
              <Image source={{ uri: champion.logo_url }} style={{ width: 74, height: 74 }} contentFit="cover" cachePolicy="memory-disk" />
            ) : (
              <AppText variant="heading" weight="bold" className="text-soft">
                {(champion.short_name ?? champion.name).slice(0, 3).toUpperCase()}
              </AppText>
            )}
          </View>
        </View>

        <View className="flex-row items-center gap-1.5">
          <Ionicons name="trophy" size={14} color={palette.captain} />
          <AppText variant="micro" weight="bold" className="uppercase tracking-widest" style={{ color: palette.captain }}>
            {t('predictor.champion')}
          </AppText>
        </View>
        <AppText variant="title" weight="bold" className="text-center text-ink" numberOfLines={1}>
          {localizeTeamName(champion, locale)}
        </AppText>
      </Card>
    </Animated.View>
  );
}

/**
 * Mobile-first knockout: each round is its OWN clean section. A round selector
 * (R32 -> Final) switches the list of match cards below; tap a team to send them
 * through and they carry into the next round. No fragile horizontal tree
 * geometry, so it stays tidy on a narrow screen. The champion is crowned on top
 * once the final is decided.
 */
export function KnockoutTree({
  teamById,
  ties,
  picks,
  championId,
  wcLogo,
  round,
  onRoundChange,
  onPick,
}: {
  teamById: Map<string, BracketTeam>;
  ties: Record<number, Tie>;
  picks: Record<number, string | null>;
  championId: string | null;
  wcLogo: string | null;
  round: RoundKey;
  onRoundChange: (round: RoundKey) => void;
  onPick: (matchId: number, teamId: string) => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const champion = championId ? teamById.get(championId) : undefined;

  return (
    <View className="gap-3">
      {champion ? <ChampionBanner champion={champion} wcLogo={wcLogo} /> : null}

      {/* Round selector */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, gap: 8 }}
      >
        {(ROUND_KEYS as readonly RoundKey[]).map((key) => {
          const active = key === round;
          return (
            <Pressable
              key={key}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => onRoundChange(key)}
              className={`rounded-full border px-4 py-1.5 ${
                active ? 'border-volt bg-volt' : 'border-line bg-surface active:bg-surface-2'
              }`}
            >
              <AppText
                variant="micro"
                weight="bold"
                style={{ color: active ? palette.onVolt : palette.soft }}
                numberOfLines={1}
              >
                {t(`predictor.round.${key}`)}
              </AppText>
            </Pressable>
          );
        })}
      </ScrollView>

      {/* Selected round's matches */}
      <View className="px-4">
        {ROUND_MATCHES[round].map((matchId) => (
          <MatchCard
            key={matchId}
            matchId={matchId}
            tie={ties[matchId]}
            pickedId={picks[matchId]}
            teamById={teamById}
            onPick={onPick}
          />
        ))}
      </View>
    </View>
  );
}
